import { Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  BAR_CHART_OPTION,
  GROUPED_BAR_CHART_OPTION,
  PIE_CHART_COLOR_MAPPING,
  PIE_CHART_OPTION
} from '@catalogues';
import {
  ChartSize,
  KpiBarChartData,
  KpiGroupedBarChartData,
  KpiKeys,
  KpiPieChartData,
  KpiSupportedToggleChartData,
  KpiToggleChart,
  KpiToggleChartData,
  ToggleLabels
} from '@models';
import { KpiChartFacadeService } from 'app/services/kpi-chart-facade/kpi-chart-facade.service';
import { KpiChartFilterService } from 'app/services/kpi-chart-filter/kpi-chart-filter.service';
import { EChartsOption } from 'echarts';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'drilldown-toggle-chart',
  templateUrl: './drilldown-toggle-chart.component.html',
  styleUrl: './drilldown-toggle-chart.component.scss'
})
export class DrilldownToggleChartComponent implements OnChanges, OnDestroy {
  @Input() toggleChartData!: KpiToggleChartData;
  @Input() chartSize!: ChartSize;
  @Input() filterService = inject(KpiChartFilterService);
  @Input() kpiKey?: KpiKeys;

  private facade = inject(KpiChartFacadeService);
  private destroy$ = new Subject<void>();

  displayedChartData?: KpiSupportedToggleChartData;
  toggleLabels!: ToggleLabels;
  toggleIsChecked!: boolean;
  toggleIsVisible!: boolean;
  legendIsVisible!: boolean;
  colorMap = PIE_CHART_COLOR_MAPPING;
  bar: EChartsOption = BAR_CHART_OPTION;
  pie: EChartsOption = PIE_CHART_OPTION;
  groupedBar: EChartsOption = GROUPED_BAR_CHART_OPTION;

  private currentKpiKey: KpiKeys | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['toggleChartData'] || changes['kpiKey']) {
      this.toggleLabels = {
        left: this.toggleChartData.charts.primaryChart.label,
        right: this.toggleChartData.charts.secondaryChart.label
      };

      // Use explicit KPI key if provided, otherwise subscribe to active KPI
      if (this.kpiKey) {
        this.currentKpiKey = this.kpiKey;
        this.initializeToggleState();
      } else {
        // Subscribe to active KPI changes to track current KPI (only once)
        if (!this.currentKpiKey) {
          this.facade.activeKpiKey$.pipe(takeUntil(this.destroy$)).subscribe((kpiKey) => {
            this.currentKpiKey = kpiKey;
            if (kpiKey && this.toggleChartData) {
              this.initializeToggleState();
            }
          });
        }

        // Initialize with current state if we already have KPI key
        if (this.currentKpiKey) {
          this.initializeToggleState();
        } else {
          // Fallback initialization without saved state
          this.displayedChartData = this.getDisplayedChart().data;
          this.setToggleIsChecked();
          this.setToggleIsVisible();
          this.setLegendIsVisible();
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onToggleClick() {
    // Determine which chart to select based on current state
    const currentlySelectedChart = this.getDisplayedChart();
    const targetChartId =
      currentlySelectedChart.id === this.toggleChartData.charts.primaryChart.id
        ? this.toggleChartData.charts.secondaryChart.id
        : this.toggleChartData.charts.primaryChart.id;

    // Update local chart data
    this.toggleChartData.charts.primaryChart.selected =
      targetChartId === this.toggleChartData.charts.primaryChart.id;
    this.toggleChartData.charts.secondaryChart.selected =
      targetChartId === this.toggleChartData.charts.secondaryChart.id;

    // Save toggle state to services if we have current KPI context
    if (this.currentKpiKey) {
      this.facade.setToggleState(
        this.currentKpiKey,
        this.toggleChartData.identifier,
        targetChartId
      );
    }

    this.displayedChartData = this.getDisplayedChart().data;
    this.setToggleIsChecked();
  }

  isBarChart(data: KpiSupportedToggleChartData): data is KpiBarChartData {
    return data.type === 'bar';
  }

  isPieChart(data: KpiSupportedToggleChartData): data is KpiPieChartData {
    return data.type === 'pie';
  }

  isGroupedBarChart(data: KpiSupportedToggleChartData): data is KpiGroupedBarChartData {
    return data.type === 'groupedBar';
  }

  private initializeToggleState(): void {
    if (!this.currentKpiKey || !this.toggleChartData) return;

    // Get saved toggle state from service
    const savedState = this.facade.getToggleState(
      this.currentKpiKey,
      this.toggleChartData.identifier
    );

    if (savedState) {
      // Apply saved state
      this.toggleChartData.charts.primaryChart.selected =
        savedState === this.toggleChartData.charts.primaryChart.id;
      this.toggleChartData.charts.secondaryChart.selected =
        savedState === this.toggleChartData.charts.secondaryChart.id;
    } else {
      // Default to primary chart if no saved state
      this.toggleChartData.charts.primaryChart.selected = true;
      this.toggleChartData.charts.secondaryChart.selected = false;

      // Save the default state
      this.facade.setToggleState(
        this.currentKpiKey,
        this.toggleChartData.identifier,
        this.toggleChartData.charts.primaryChart.id
      );
    }

    // Update the displayed chart data and toggle state after initialization
    this.displayedChartData = this.getDisplayedChart().data;
    this.setToggleIsChecked();
    this.setToggleIsVisible();
    this.setLegendIsVisible();
  }

  private setToggleIsVisible(): void {
    this.toggleIsVisible = this.chartSize !== 'small' && this.dataAvailable();
  }

  private setToggleIsChecked() {
    this.toggleIsChecked = this.toggleChartData.charts.secondaryChart.selected;
  }

  private getDisplayedChart(): KpiToggleChart {
    return (
      Object.values(this.toggleChartData.charts).find((chart) => chart.selected) ||
      this.toggleChartData.charts.primaryChart
    );
  }

  private setLegendIsVisible(): void {
    this.legendIsVisible =
      this.displayedChartData !== undefined &&
      this.displayedChartData.legendValues.length > 0 &&
      this.dataAvailable();
  }

  private dataAvailable(): boolean {
    return Object.keys(this.displayedChartData?.values).length > 0;
  }
}
